import { Button } from '@components/button'
import { Input } from '@components/input'
import { Typography } from '@components/typography'
import { WidgetReadyBoundary } from '@components/widget-ready-boundary'
import { Login, RequestReset } from '@haus-storefront-react/authentication'
import { Activity, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AuthenticationAttributes } from './_propsFn'
import styles from './Authentication.module.scss'

const AuthenticationWidget = ({ configStyles }: AuthenticationAttributes) => {
  const { t } = useTranslation()
  const idPrefix = useId().replace(/:/g, '')
  const loginFormErrorId = `${idPrefix}-login-form-error`
  const loginEmailInputId = `${idPrefix}-login-email`
  const loginPasswordInputId = `${idPrefix}-login-password`
  const requestResetErrorId = `${idPrefix}-request-reset-error`
  const requestResetEmailInputId = `${idPrefix}-request-reset-email`
  const [showRequestReset, setShowRequestReset] = useState(false)

  const labelTypography = configStyles?.labelTypography
  const buttonVariant = configStyles?.loginButtonVariant?.variant ?? 'primary'
  const requestResetBackToLoginButtonVariant =
    configStyles?.requestResetBackToLoginButtonVariant?.variant ?? 'link'
  const forgotPasswordButtonVariant = configStyles?.forgotPasswordButtonVariant?.variant ?? 'link'

  const handleLoginSuccess = () => {
    window.location.href = t('urls.account')
  }

  const handleOnError = (error: string | undefined) => {
    switch (error) {
      case 'INVALID_CREDENTIALS_ERROR':
        return t('account.login.invalidCredentialsErrorMessage')

      default:
        return t('general.genericErrorMessage')
    }
  }

  return (
    <WidgetReadyBoundary isReady>
      <div>
        <Activity mode={showRequestReset ? 'hidden' : 'visible'}>
          <Login.Root onLogin={handleLoginSuccess}>
            {({ loginError, isLoading }) => {
              return (
                <Login.Form
                  aria-describedby={loginError ? loginFormErrorId : undefined}
                  className={styles.form}
                >
                  <div className={styles.inputGroup}>
                    <Typography
                      as="span"
                      variant={labelTypography?.variant ?? 'body'}
                      color={labelTypography?.color ?? 'body'}
                      className={styles.label}
                    >
                      <label htmlFor={loginEmailInputId}>{t('account.login.emailLabel')}</label>
                    </Typography>
                    <Login.EmailInput asChild>
                      <Input
                        id={loginEmailInputId}
                        autoComplete="email"
                        aria-invalid={loginError ? 'true' : undefined}
                        aria-describedby={loginError ? loginFormErrorId : undefined}
                      />
                    </Login.EmailInput>
                  </div>

                  <div className={styles.inputGroup}>
                    <Typography
                      as="span"
                      variant={labelTypography?.variant ?? 'body'}
                      color={labelTypography?.color ?? 'body'}
                      className={styles.label}
                    >
                      <label htmlFor={loginPasswordInputId}>
                        {t('account.login.passwordLabel')}
                      </label>
                    </Typography>
                    <Login.PasswordInput asChild>
                      <Input
                        id={loginPasswordInputId}
                        autoComplete="current-password"
                        aria-invalid={loginError ? 'true' : undefined}
                        aria-describedby={loginError ? loginFormErrorId : undefined}
                      />
                    </Login.PasswordInput>
                  </div>

                  {loginError && (
                    <div className={styles.errorMessage} id={loginFormErrorId} role="alert">
                      {handleOnError(loginError.message)}
                    </div>
                  )}

                  <div className={styles.loginActions}>
                    <Login.SubmitButton asChild>
                      <Button loading={isLoading} variant={buttonVariant}>
                        {t('account.login.submitButton')}
                      </Button>
                    </Login.SubmitButton>

                    <Button
                      type="button"
                      variant={forgotPasswordButtonVariant}
                      onClick={() => {
                        setShowRequestReset(true)
                      }}
                      className={styles.link}
                    >
                      {t('account.requestReset.forgotPasswordLink')}
                    </Button>
                  </div>
                </Login.Form>
              )
            }}
          </Login.Root>
        </Activity>

        <Activity mode={showRequestReset ? 'visible' : 'hidden'}>
          <RequestReset.Root>
            {({ requestResetError, isLoading, requestResetSuccess }) => (
              <RequestReset.Form
                className={styles.form}
                aria-describedby={requestResetError ? requestResetErrorId : undefined}
              >
                <div className={styles.inputGroup}>
                  <Typography
                    as="span"
                    variant={labelTypography?.variant ?? 'body'}
                    color={labelTypography?.color ?? 'body'}
                    className={styles.label}
                  >
                    <label htmlFor={requestResetEmailInputId}>
                      {t('account.requestReset.emailLabel')}
                    </label>
                  </Typography>
                  <RequestReset.EmailInput asChild>
                    <Input
                      id={requestResetEmailInputId}
                      aria-invalid={requestResetError ? 'true' : undefined}
                      aria-describedby={requestResetError ? requestResetErrorId : undefined}
                      autoComplete="email"
                    />
                  </RequestReset.EmailInput>
                </div>

                {requestResetError && (
                  <div className={styles.errorMessage} id={requestResetErrorId} role="alert">
                    {t('account.requestReset.errorMessage')}
                  </div>
                )}
                {requestResetSuccess && (
                  <div className={styles.successMessage} role="status">
                    {t('account.requestReset.successMessage')}
                  </div>
                )}
                <div className={styles.requestResetButtons}>
                  <RequestReset.SubmitButton asChild className={styles.submitButton}>
                    <Button loading={isLoading} variant="primary">
                      {t('account.requestReset.submitButton')}
                    </Button>
                  </RequestReset.SubmitButton>
                  <Button
                    type="button"
                    onClick={() => setShowRequestReset(false)}
                    variant={requestResetBackToLoginButtonVariant}
                    className={styles.link}
                  >
                    {t('account.requestReset.backToLogin')}
                  </Button>
                </div>
              </RequestReset.Form>
            )}
          </RequestReset.Root>
        </Activity>
      </div>
    </WidgetReadyBoundary>
  )
}

export default AuthenticationWidget
